import React from "react";

import { IClass, INotification } from "../../../state";
import { ModalContext } from "../../../libs/ModalProvider";
import { convertLink } from "../../../libs/Util";
import LinkDialog from "../../../molecules/LinkDialog";
import linkIcon from "../../../assets/icon/link.svg";

import styles from "./NotificationForm.module.scss";

export interface NotificationFormProps {
  title: string;
  calling: boolean;
  error: string;
  classes: IClass[];
  notification?: INotification;
  upsertNotification: (notification: {
    id: number;
    content: string;
    for_teacher: boolean;
    class_ids: number[];
  }) => void;
}

const validates: { [key: string]: (value: any) => string } = {};

validates["class_ids"] = val => {
  return val.class_ids.length === 0 && !val.for_teacher
    ? "送信先を選択してください"
    : "";
};
validates["content"] = val => {
  return val.content ? "" : "通知内容を入力してください。";
};

export const NotificationForm = (props: NotificationFormProps) => {
  const ctx = React.useContext(ModalContext);
  const [state, setState] = React.useState({
    id: props.notification ? props.notification.id : 0,
    content: props.notification ? props.notification.content : "",
    for_teacher: props.notification ? props.notification.for_teacher : false,
    class_ids: props.notification ? props.notification.class_ids : [],
    isConfirm: false,
    isSubmitted: false,
    errorMessages: {} as { [key: string]: string },
  });

  React.useEffect(() => {
    setState(s => {
      if (props.notification) {
        return {
          ...s,
          id: props.notification ? props.notification.id : 0,
          content: props.notification ? props.notification.content : "",
          for_teacher: props.notification
            ? props.notification.for_teacher
            : false,
          class_ids: props.notification ? props.notification.class_ids : [],
        };
      } else {
        return s;
      }
    });
  }, [props.notification]);

  const onConfirm = React.useCallback(() => {
    const errorMessages = {} as { [key: string]: string };
    Object.keys(validates).forEach(key => {
      const ret = validates[key](state);
      if (ret) {
        errorMessages[key] = ret;
      }
    });
    if (Object.keys(errorMessages).length > 0) {
      setState({ ...state, errorMessages });
      return;
    }
    setState({ ...state, errorMessages: {}, isConfirm: true });
  }, [state]);

  const onSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      props.upsertNotification({
        id: state.id,
        content: state.content,
        for_teacher: state.for_teacher,
        class_ids: state.class_ids,
      });
    },
    [state, props],
  );

  return (
    <div>
      <div className={styles.title}>
        <p>{props.title}</p>
        <span>GEARのお知らせを新規作成します。</span>
      </div>
      {state.isConfirm ? (
        <>
          <div className={styles.box}>
            送信先:
            <div className={styles.value}>
              {state.class_ids.length === props.classes.length
                ? "全校"
                : state.class_ids
                    .map(id => {
                      const c = props.classes.filter(cl => cl.id === id)[0];
                      return c.full_name;
                    })
                    .join(",")}
              {state.for_teacher &&
                (state.class_ids.length === 0 ? "先生" : "および先生")}
            </div>
            お知らせ内容:
            <div
              className={styles.value}
              dangerouslySetInnerHTML={{ __html: convertLink(state.content) }}
            />
          </div>
          <div className={styles.btnArea}>
            <button
              className={"btn btn-secondary"}
              disabled={props.calling}
              onClick={() => setState({ ...state, isConfirm: false })}
            >
              修正する
            </button>
            <button
              className={"btn " + styles.okBtn}
              disabled={props.calling}
              onClick={onSubmit}
            >
              お知らせを投稿する
            </button>
          </div>
        </>
      ) : (
        <>
          <div className={styles.subTitle}>送信先を選択</div>
          <div className={styles.selectBox}>
            <div className={styles.leftPart}>
              <input
                type="checkbox"
                id="checkall"
                className={styles.checkbox}
                onChange={() => {
                  state.class_ids.length === props.classes.length
                    ? setState({ ...state, class_ids: [] })
                    : setState({
                        ...state,
                        class_ids: props.classes.map(c => c.id),
                      });
                }}
                checked={state.class_ids.length === props.classes.length}
              />
              <label htmlFor="checkall" className={styles.label}>
                全校
              </label>
            </div>
            <div className={styles.rightPart}>
              {props.classes.map(c => {
                return (
                  <React.Fragment key={c.id}>
                    <input
                      type="checkbox"
                      id={`check${c.id}`}
                      value={c.id}
                      checked={state.class_ids.indexOf(c.id) !== -1}
                      onChange={e =>
                        e.target.checked
                          ? setState({
                              ...state,
                              class_ids: [...state.class_ids, c.id],
                            })
                          : setState({
                              ...state,
                              class_ids: state.class_ids.filter(
                                id => id !== c.id,
                              ),
                            })
                      }
                    />
                    <label htmlFor={`check${c.id}`} className={styles.label}>
                      {c.full_name}
                    </label>
                  </React.Fragment>
                );
              })}
            </div>
            <div className={styles.leftPart}>
              <input
                type="checkbox"
                id="checkteacher"
                className={styles.checkbox}
                onChange={() =>
                  setState({ ...state, for_teacher: !state.for_teacher })
                }
                checked={state.for_teacher}
              />
              <label
                htmlFor="checkteacher"
                className={styles.label + " " + styles.lastLabel}
              >
                先生
              </label>
            </div>
          </div>
          {state.errorMessages["class_ids"] && (
            <div className={styles.caution}>
              {state.errorMessages["class_ids"]}
            </div>
          )}
          <div className={styles.subTitle}>通知内容</div>
          <div className={styles.textBox}>
            <textarea
              value={state.content}
              onChange={e => setState({ ...state, content: e.target.value })}
              className={styles.textArea}
            />
            <img
              src={linkIcon}
              className={styles.linkIcon}
              alt="link"
              onClick={() => {
                ctx.actions.showModal(
                  <LinkDialog
                    closeModal={() => {
                      ctx.actions.closeModal();
                    }}
                    callback={(title: string, url: string) => {
                      setState({
                        ...state,
                        content:
                          state.content + `[${title ? title : url}](${url})`,
                      });
                    }}
                  />,
                );
              }}
            />
          </div>
          {state.errorMessages["content"] && (
            <div className={styles.caution}>
              {state.errorMessages["content"]}
            </div>
          )}
          <div className={styles.btnArea}>
            <button
              className={"btn btn-secondary"}
              onClick={() => window.history.back()}
            >
              キャンセル
            </button>
            <button
              className={"btn " + styles.okBtn}
              disabled={props.calling}
              onClick={onConfirm}
            >
              お知らせ内容を確認する
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationForm;
